import { z } from 'zod';
import { queryBoolean } from '../api/query-boolean';

/**
 * Broadcast email/SMS/WhatsApp campaigns.
 *
 * A campaign is composed as a DRAFT, sent exactly once, and never edited
 * again afterward — see `communications.prisma` for why v1 stays this thin.
 */

export const CAMPAIGN_CHANNELS = ['EMAIL', 'SMS', 'WHATSAPP'] as const;
export type CampaignChannel = (typeof CAMPAIGN_CHANNELS)[number];

export const CAMPAIGN_STATUSES = ['DRAFT', 'SENDING', 'SENT', 'FAILED'] as const;
export type CampaignStatus = (typeof CAMPAIGN_STATUSES)[number];

export const AUDIENCE_KINDS = ['ALL', 'TAGS', 'PERSON_IDS', 'GROUP'] as const;
export type AudienceKind = (typeof AUDIENCE_KINDS)[number];

export const createCampaignRequestSchema = z
  .object({
    channel: z.enum(CAMPAIGN_CHANNELS),
    /** Required for EMAIL, refused for SMS and WHATSAPP — neither has a subject line. */
    subject: z.string().trim().min(1).max(200).nullish(),
    body: z.string().trim().min(1).max(4000),
    audienceKind: z.enum(AUDIENCE_KINDS),
    /** Required (non-empty) when audienceKind is TAGS, ignored otherwise. */
    audienceTagIds: z.array(z.uuid()).max(20).default([]),
    /** Required (non-empty) when audienceKind is PERSON_IDS — a single
     *  profile's "send a message" action sends one id; a People-list
     *  selection can send several. */
    audiencePersonIds: z.array(z.uuid()).max(500).default([]),
    /** Required when audienceKind is GROUP. */
    audienceGroupId: z.uuid().nullish(),
  })
  .refine((value) => value.channel !== 'EMAIL' || Boolean(value.subject?.trim()), {
    message: 'An email campaign requires a subject',
    path: ['subject'],
  })
  .refine((value) => value.audienceKind !== 'TAGS' || value.audienceTagIds.length > 0, {
    message: 'Select at least one tag',
    path: ['audienceTagIds'],
  })
  .refine((value) => value.audienceKind !== 'PERSON_IDS' || value.audiencePersonIds.length > 0, {
    message: 'Select at least one person',
    path: ['audiencePersonIds'],
  })
  .refine((value) => value.audienceKind !== 'GROUP' || value.audienceGroupId != null, {
    message: 'Select a group',
    path: ['audienceGroupId'],
  });
export type CreateCampaignRequest = z.infer<typeof createCampaignRequestSchema>;

/** Same shape as create — a draft is replaced wholesale rather than
 *  patched field by field, since every field is required to send anyway. */
export const updateCampaignRequestSchema = createCampaignRequestSchema;
export type UpdateCampaignRequest = z.infer<typeof updateCampaignRequestSchema>;

export const audiencePreviewRequestSchema = z.object({
  channel: z.enum(CAMPAIGN_CHANNELS),
  audienceKind: z.enum(AUDIENCE_KINDS),
  audienceTagIds: z.array(z.uuid()).max(20).default([]),
  audiencePersonIds: z.array(z.uuid()).max(500).default([]),
  audienceGroupId: z.uuid().nullish(),
});
export type AudiencePreviewRequest = z.infer<typeof audiencePreviewRequestSchema>;

export const listCampaignsQuerySchema = z.object({
  cursor: z.string().nullish(),
  limit: z.coerce.number().int().min(1).max(100).default(25),
  status: z.enum(CAMPAIGN_STATUSES).nullish(),
  includeArchived: queryBoolean().default(false),
});
export type ListCampaignsQuery = z.infer<typeof listCampaignsQuerySchema>;

// --- Responses ----------------------------------------------------------------

export interface CampaignResponse {
  readonly id: string;
  readonly channel: CampaignChannel;
  readonly status: CampaignStatus;
  readonly subject: string | null;
  readonly body: string;
  readonly audienceKind: AudienceKind;
  readonly audienceTagIds: readonly string[];
  readonly audiencePersonIds: readonly string[];
  readonly audienceGroupId: string | null;
  readonly recipientCount: number;
  readonly sentCount: number;
  readonly failedCount: number;
  readonly skippedCount: number;
  readonly createdAt: string;
  readonly updatedAt: string;
  readonly sentAt: string | null;
  readonly createdByUserId: string;
  readonly archivedAt: string | null;
}

/** How many people currently match a draft's audience filter — rendered in
 *  the compose screen before the operator commits to sending. */
export interface AudiencePreviewResponse {
  readonly matchedCount: number;
  /** Of `matchedCount`, how many have no address for this channel or have
   *  opted out — the difference is exactly what `recipientCount` will be
   *  once sent. */
  readonly reachableCount: number;
}
